"use client";

import clsx from "clsx";
import Image from "next/image";
import {useContext} from "react";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import {ReviewConversationContext} from "@/contexts/chat/MutationProposalReviewContext";
import type {ApprovalVisualState} from "@repo/ui/gailgpt/approval-status-banner";

const ICON_BUTTON = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-500 hover:bg-700/10 hover:text-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

interface MutationProposalReviewBarProps {
   title: React.ReactNode;
   subtitle: React.ReactNode;
   onClose: () => void;
   /** Decision controls, placed before the close button. */
   actions?: React.ReactNode;
   /** Notices under the bar's header row. */
   children?: React.ReactNode;
   role?: "status";
   className?: string;
   banner?: React.ReactNode;
   approvalState?: ApprovalVisualState;
}

/** Floats above the editor while you scroll it, so the decision is always one click away. */
export default function MutationProposalReviewBar({title, subtitle, onClose, actions, children, role, className, banner, approvalState}: MutationProposalReviewBarProps) {
   const {t} = useTranslation("ask");
   const conversation = useContext(ReviewConversationContext);
   return <div role={role} data-approval-state={approvalState} className={clsx("approval-card sticky top-4 z-[5] mb-3 rounded-xl border shadow-sm", className)}>
      {banner}
      <div className="px-4 py-3.5">
         <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
            <div className="flex min-w-0 flex-1 basis-48 items-center gap-3">
               <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950/50">
                  <Image src="/icons/gail2-icon-blue.svg" alt="" width={18} height={18} className="h-[18px] w-[18px]" />
               </span>
               <div className="min-w-0">
                  {title}
                  {subtitle}
               </div>
            </div>
            {/* On a phone the decision gets its own row, so the bar never runs off the screen. */}
            {actions && <div className="order-last flex w-full flex-wrap items-center justify-end gap-2 md:order-none md:ml-auto md:w-auto md:shrink-0">
               {actions}
            </div>}
            <div className="ml-auto flex shrink-0 items-center gap-1 md:ml-0">
               {conversation?.open && <button type="button" onClick={conversation.open} aria-label={t("toolApproval.openConversation")} title={t("toolApproval.openConversation")}
                  className={clsx(ICON_BUTTON, conversation.openBelowMdOnly && "md:hidden")}>
                  <Icon icon="comment" className="h-4 w-4" />
               </button>}
               <button type="button" onClick={onClose} aria-label={t("toolApproval.closeReview")} title={t("toolApproval.closeReview")} className={ICON_BUTTON}>
                  <Icon icon="xmark" className="h-4 w-4" />
               </button>
            </div>
         </div>
         {children}
      </div>
   </div>;
}
